// RC18.2 Phase 3 — a flat, permission-gated registry of top-level pages for the Command
// Palette's "jump to page" list and (RC18.7) the drawer's Favorites section. Deliberately
// independent of `Layout.js`'s own nav-item arrays rather than a shared refactor of both —
// `Layout.js`'s nav structure is covered by an extensive existing test suite and reshaping it
// into a shared registry is real risk. What RC18.7 *does* fix (Phase 1 audit finding: this
// registry had drifted to cover barely half the app's real routes, so Ctrl+K and Favorites
// couldn't reach Security/Platform/Identity/Customer pages at all) is completeness — every
// static top-level route in App.js now has an entry here, kept in the same grouping order as
// Layout.js's nav groups for readability.
//
// `permission` may be a single code or an array — an array means "any of these" (RC18.6.2's
// AdminRoute established this same any-of shape for the identical reason: Security Groups and a
// few other pages are reachable by two different permission tiers).
const pageRegistry = [
  { label: 'User Guide', path: '/user-guide' },
  { label: 'Dashboard', path: '/dashboard' },

  // Candidates & Resumes
  { label: 'Candidates', path: '/candidates' },
  { label: 'Resumes', path: '/resumes' },

  // Clients & Vendors
  { label: 'Clients', path: '/clients' },
  { label: 'Vendors', path: '/vendors' },
  { label: 'Vendor Analytics', path: '/vendor-analytics' },
  { label: 'Market Intelligence', path: '/market-intelligence' },

  // Recruiting Pipeline
  { label: 'Jobs', path: '/jobs' },
  { label: 'Submissions', path: '/submissions' },
  { label: 'Pipeline', path: '/pipeline' },
  { label: 'Interviews', path: '/interviews' },
  { label: 'Interview Intelligence', path: '/interview-intelligence', permission: 'InterviewIntelligence.View' },
  { label: 'Offers', path: '/offers' },

  // Workforce
  { label: 'Placements', path: '/placements', permission: 'Placement.View' },
  { label: 'Consultants', path: '/consultants', permission: 'Consultant.View' },
  { label: 'Bench', path: '/bench', permission: 'Bench.View' },

  // Productivity
  { label: 'My Tasks', path: '/tasks' },
  { label: 'Calendar', path: '/calendar', permission: 'Calendar.View' },
  { label: 'Email Templates', path: '/email-templates' },
  { label: 'Communication Center', path: '/communications', permission: 'Communication.View' },
  { label: 'Notifications', path: '/notifications' },
  { label: 'Activity Timeline', path: '/activity' },

  // Insights
  { label: 'Knowledge Base', path: '/knowledge', permission: 'Knowledge.View' },
  { label: 'Reports', path: '/reporting', permission: 'Reporting.View' },
  { label: 'Dashboards', path: '/reporting/dashboards', permission: 'Reporting.Dashboard' },
  { label: 'Performance', path: '/performance' },
  { label: 'Executive Dashboard', path: '/executive', permission: 'Forecast.View' },
  { label: 'My Organization', path: '/customer/portal', permission: 'Customer.View' },

  // Monitoring
  { label: 'Operations Center', path: '/operations', permission: 'Operations.View' },
  { label: 'System Logs', path: '/system-logs', permission: 'Logs.View' },
  { label: 'Backups', path: '/backups', permission: 'Backup.View' },
  { label: 'Integration Center', path: '/integrations', permission: 'Integration.View' },

  // Tenant Administration
  { label: 'Users', path: '/admin/users', permission: 'User.View' },
  { label: 'Security Groups', path: '/admin/security-groups', permission: ['SecurityGroup.View', 'TenantSecurityGroup.View'] },
  { label: 'Permission Profiles', path: '/admin/permission-profiles', permission: 'PermissionProfile.View' },
  { label: 'Permissions', path: '/admin/permissions', permission: 'Permission.View' },
  { label: 'Effective Permissions', path: '/admin/security/effective-permissions', permission: 'Security.View' },
  { label: 'Permission Simulator', path: '/admin/security/simulator', permission: 'Security.View' },
  { label: 'Security Audit', path: '/admin/security', permission: 'Security.View' },
  { label: 'Permission Groups', path: '/admin/permission-groups', permission: 'Permission.Manage' },
  { label: 'Service Accounts', path: '/admin/service-accounts', permission: 'ServiceAccount.View' },
  { label: 'Developer Portal', path: '/admin/developer-portal', permission: 'PublicApi.View' },
  // PR-REL-005 — Identity Center removed from this registry too: an Identity.View holder must
  // not be able to jump to it via Ctrl+K/Command Palette or Favorites/Recent even though it's
  // gone from Layout.js's own nav (see App.js's route comment for why). Re-add alongside
  // Layout.js's nav entry once real SSO/SCIM federation exists.
  { label: 'Feature Management', path: '/admin/feature-management', permission: 'FeatureManagement.View' },
  { label: 'Data Import', path: '/admin/data-import', permission: 'DataImport.View' },

  // Platform Administration
  { label: 'Platform Dashboard', path: '/platform/dashboard', permission: 'Platform.View' },
  { label: 'Tenant Switcher', path: '/platform/tenant-switcher', permission: 'Tenant.Switch' },
  { label: 'Tenants', path: '/admin/tenants', permission: 'Tenant.View' },
  { label: 'System Incidents', path: '/platform/incidents', permission: 'Platform.View' },
  { label: 'Platform System Logs', path: '/platform/system-logs', permission: 'Platform.View' },
  { label: 'Workflow Monitor', path: '/platform/workflows', permission: 'System.Operate' },
  { label: 'Backup Monitor', path: '/platform/backups', permission: 'Platform.View' },
  { label: 'Production Dashboard', path: '/platform/production-dashboard', permission: 'Platform.View' },
  { label: 'Module Health', path: '/platform/module-health', permission: 'Platform.View' },
  { label: 'Configuration Center', path: '/platform/configuration', permission: 'Platform.Manage' },
  { label: 'Enterprise Operations Center', path: '/platform/enterprise-operations', permission: 'EnterpriseOperations.View' },
];

export default pageRegistry;
